/**
 * GA4 Tracker Interface
 * @author Padrin Panji <padrin@99.co>
 * @since 2023.06.27
 * @docs https://docs.google.com/spreadsheets/d/1KoZ4q66ui0Jlw-ow9esRVBmKWCeF6xA5KvY774UgpnE/edit#gid=240724405&range=A1
 */
import { Maybe, Scalars } from '@/modules/core/gql/generated';
import { AuthDataReducerInterface } from '@/modules/tracker/tracker.type';

import { LangType } from '../[lang]/_type/lang';

export interface GA4ParamInterface {
    eventAction: GA4EventActionEnum;
    pageType: GA4PageType;
    featureType: GA4FeatureType;
    listing?: SimplestGA4ListingTracker;
    authData: AuthDataReducerInterface;
    language: LangType;
    token: string;
    position?: number;
    search?: GA4SearchParam;
}

export interface SimplestGA4ListingTracker {
    originId: { formattedValue: Scalars['String'] };
    propertyType: { value: Scalars['String'] };
    priceType: { value: Scalars['String'] };
    location: {
        city: { name: string };
        district: { name: string };
        province: { name: string };
    };
    specialFlag?: Maybe<{ isPrimaryProject: Scalars['Boolean'] }>;
    instanceInfo?: Maybe<{ status: Scalars['Int'] }>;
    subscriptionTierId: Scalars['Int'];
    primaryProject?: Maybe<{ projectType?: Maybe<Scalars['Int']> }>;
    agent?: Maybe<{
        name: Scalars['String'];
        originId: Scalars['String'];
        subscription?: Maybe<{
            type?: Maybe<{ name?: Maybe<Scalars['String']> }>;
        }>;
        organization?: Maybe<{ name: Scalars['String'] }>;
    }>;
}
export interface GA4GTMParamInterface {
    event: 'r123BaseTrigger';
    eventAction: GA4EventActionEnum;
    experiment?: GA4ExperimentParam;
    listing?: GA4ListingParam;
    page: GA4PageParam;
    seller?: GA4SellerParam;
    user: GA4UserParam;
    search?: GA4SearchParam;
    mortgage?: GA4MortgageParam;
    transaction?: GA4TransactionParam;
}

export type GA4ExperimentParam = {
    name: string;
};

export type GA4ListingParam = {
    id: string;
    marketType: string;
    locationLevel1: string;
    locationLevel2: string;
    locationLevel3: string;
    area: string | null;
    productName: string;
    cardPosition: number;
};

export type GA4PageParam = {
    type: GA4PageType;
    feature: GA4FeatureType;
    onePageBefore: string | null;
    twoPageBefore: string | null;
    onePageFeatureBefore: GA4FeatureType | null;
    twoPageFeatureBefore: GA4FeatureType | null;
};

export type GA4SellerParam = {
    info: string;
    company: string;
};

export type GA4UserParam = {
    phoneNumber: string;
    email: string;
    name: string;
    location: string | null;
    numberOfVisit: string;
    type: GA4UserType;
    sessionIdentifier: string;
    acquisitionUrl: string;
    landingUrl: string;
};

export type GA4SearchParam = {
    query: string;
    type: string;
};

export type GA4MortgageParam = {
    bankName: string;
    bankProductName: string;
};

export type GA4TransactionParam = {
    paymentMethod: string;
    productPurchaseType: string;
};

export enum GA4EventActionEnum {
    Enquiry = 'enquiry',
    LDPExpandClick = 'ldp_expand_click',
    LDPListingImpression = 'ldp_section_impression',
    ListingClick = 'listing_click',
    ListingImpression = 'listing_impression',
    SearchSubmission = 'search_submission',
    OTPFormSubmitted = 'otp_form_submitted',
    OTPNumberSubmitted = 'otp_number_submitted',
    OTPUnverifiedClick = 'otp_unverified_click',
    BulkEnquiry = 'bulk_enquiry',
    ResendOTP = 'resend_otp',
    FABClick = 'fab_click',
    FABImpression = 'fab_impression',
    SplashScreenClick = 'splash_screen_click',
    SplashScreenImpression = 'splash_screen_impression',
    SplashScreenClose = 'splash_screen_close',
    SplashScreenConfirmClose = 'splash_screen_confirm_close',
    SplashScreenDontShow = 'splash_screen_dont_show',
}

export type GA4UserType =
    | 'User Unregistered'
    | 'User Registered Not Logged In'
    | 'User Registered Logged In'
    | 'Homeowner Individual Listing'
    | string; // This Type Only for Subscription Agent

export type GA4PageType =
    | 'HP'
    | 'Homepage'
    | 'NHP'
    | 'SRP'
    | 'NSRP'
    | 'LDP'
    | 'UDP'
    | 'PDP'
    | 'POI Page'
    | 'Explore Page'
    | 'Recommendation Page'
    | 'Handpicked Page'
    | 'Favorite Listing'
    | 'Recently Viewed'
    | 'Saved Search'
    | 'Short Video Player'
    | 'Price Drop'
    | 'Login Page'
    | 'Aset Bank'
    | 'Agent Profile'
    | 'Special Offer';

export type GA4FeatureType =
    | 'Recommendation Widget'
    | 'Netcore Recommendation Widget'
    | 'Properti Pilihan Kami'
    | 'Properti Terpopuler'
    | 'Daftar Properti Baru'
    | 'Perumahan Baru Terdekat'
    | 'Properti Pilihan Untukmu'
    | 'Galeri Properti'
    | 'Official Developer'
    | 'Cari Hunian Impianmu'
    | 'Daftar Properti Baru'
    | 'SRP Card'
    | 'LDP Whatsapp'
    | 'LDP Phone'
    | 'Primary Section'
    | 'Post Enquiry Suggestion'
    | 'UDP Section'
    | 'Project Lainnya'
    | 'Recommendation Card'
    | 'Handpicked Card'
    | 'Favorite Listing Card'
    | 'Recently Viewed Card'
    | 'Saved Search Card'
    | 'Short Video Phone'
    | 'Short Video Whatsapp'
    | 'Click "Pilih Semua"'
    | 'Unit Lainnya Section'
    | 'Point of Interest Section'
    | 'Description Section'
    | 'Specification Section'
    | 'PDP Splash Screen'
    | 'Share'
    | string; // This Type Only for POI and Explore Page
